import { ArrowDown2 } from '@/components/icons';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { ComponentType, ReactNode } from 'react';

interface CollapsibleSectionProps {
    title: string;
    /** Optional leading iconsax glyph, matching the Personalizar modal sections. */
    icon?: ComponentType<{ className?: string }>;
    /**
     * Current value of the section, shown on the trigger row so the setting can
     * be read without opening it (e.g. "Georgia · serifada").
     */
    summary?: ReactNode;
    /** Dims the row when the section has nothing to act on in this laudo. */
    muted?: boolean;
    open: boolean;
    onToggle: () => void;
    /** Replaces the default `space-y-4` content panel spacing. */
    contentClassName?: string;
    /** Put on the trigger, so tests survive a label rename. */
    testId?: string;
    children: ReactNode;
}

/**
 * A collapsible settings group — a bordered trigger row (optional icon + label +
 * current-value summary + rotating chevron) over a bordered content panel. Used
 * by the Personalizar (Customize) modal and the chart config so the two read the
 * same. Single-open behavior is orchestrated by the caller (pass `open`/`onToggle`).
 */
export default function CollapsibleSection({
    title,
    icon: Icon,
    summary,
    muted = false,
    open,
    onToggle,
    contentClassName,
    testId,
    children,
}: CollapsibleSectionProps) {
    return (
        <Collapsible open={open} onOpenChange={onToggle}>
            <CollapsibleTrigger
                data-testid={testId}
                className={cn(
                    'flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-surface',
                    muted && 'opacity-60',
                )}
            >
                <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold">
                    {Icon && <Icon className="size-4 flex-shrink-0 text-muted-foreground" />}
                    <span className="truncate">{title}</span>
                </span>
                {summary && !open ? (
                    <span className="min-w-0 truncate text-xs text-muted-foreground">
                        {summary}
                    </span>
                ) : null}
                <ArrowDown2
                    className={cn(
                        'size-4 flex-shrink-0 text-muted-foreground transition-transform',
                        open && 'rotate-180',
                    )}
                />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
                <div
                    className={cn(
                        'rounded-lg border border-border bg-card p-3',
                        contentClassName ?? 'space-y-4',
                    )}
                >
                    {children}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}
